// Display-only billing classification for a class. Derives how a session is
// billed purely from existing data — it NEVER changes stored data, billing,
// packs, GST, due dates or credit rules. Returns null when there is nothing
// to show (e.g. a scheduled pay-per-session class not yet completed).
//
// A pack credit is spent at BOOKING time (session.package_id is set). The
// completion branch only creates an invoice when the session has no
// package_id, so a healthy pack class has a credit and no invoice, and a
// healthy pay-per-session class has an invoice and no credit.
export function getBilling(session, ctx = {}) {
  if (!session) return null;
  const { invoiceBySession = {}, packById = {}, invoiceByPackage = {} } = ctx;

  const ownInvoice = invoiceBySession[session.session_id] || null;
  const hasInvoice = !!ownInvoice;
  const hasPack = !!session.package_id;

  // Both a credit and its own invoice — never expected; flag for review.
  if (hasPack && hasInvoice) return { kind: "check" };

  // Covered by a pack credit (scheduled or completed).
  if (hasPack) {
    const pkg = packById[session.package_id] || null;
    const packInvoice = invoiceByPackage[session.package_id] || null;
    return {
      kind: "pack",
      packReturned: session.status === "cancelled" && !!session.pack_refunded,
      packInvoiceNumber: packInvoice?.invoice_number || null,
      packInvoiceId: packInvoice?.invoice_id || null,
      packName: pkg?.name || null,
      packRemaining: pkg ? Number(pkg.sessions_remaining ?? 0) : null,
      packTotal: pkg ? Number(pkg.total_sessions ?? 0) : null,
    };
  }

  // Its own invoice.
  if (hasInvoice) {
    return {
      kind: "own",
      invoiceNumber: ownInvoice.invoice_number,
      invoiceId: ownInvoice.invoice_id,
      total: Number(ownInvoice.total || 0),
      status: ownInvoice.status,
    };
  }

  // Completed, yet neither invoiced nor covered by a credit.
  if (session.status === "completed") return { kind: "none" };

  return null;
}
